import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { X, Copy, ExternalLink, Printer, QrCode } from 'lucide-react';
import SaleQRCode from './SaleQRCode';
import { CARD_BUY_URL } from '../../../pages/CardQRPrintPage';

const CY = '#90E0EF';

// Full-screen permanent QR overlay for the self-service card flow.
// The QR always points at the production URL (it gets printed for the fair);
// OPEN CHECKOUT opens the current environment so admins can preview the form.
export default function CardSaleModal({ open, onClose }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const copy = () => {
    navigator.clipboard.writeText(CARD_BUY_URL);
    setCopied(true);
    toast.success('Link copied');
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] bg-[#050505] admin-root overflow-y-auto" data-testid="card-sale-modal">
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'linear-gradient(#90E0EF 1px, transparent 1px), linear-gradient(90deg, #90E0EF 1px, transparent 1px)', backgroundSize: '48px 48px' }} />

        <div className="sticky top-0 z-10 flex items-center justify-between px-6 h-16 border-b border-[#161616] bg-[#050505]/90 backdrop-blur">
          <div className="flex items-center gap-2.5">
            <QrCode className="w-4 h-4" style={{ color: CY }} />
            <span className="font-outfit font-semibold text-white tracking-tight">Card Sale · Permanent QR</span>
          </div>
          <button onClick={onClose} data-testid="card-sale-close" style={{ cursor: 'pointer' }} className="text-[#888] hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="relative z-[1] max-w-lg mx-auto px-6 py-12 text-center">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CY }}>Mr. Relative</p>
          <h1 className="font-outfit font-bold text-white tracking-tight mt-3" style={{ fontSize: '2.2rem', lineHeight: 1.03 }}>Sell cards at the stand</h1>
          <p className="text-xs font-mono text-[#888] mt-3 leading-relaxed">
            One permanent QR. The collector scans it, enters their card numbers, and pays £20 per card. No reservation — the first completed payment secures the edition.
          </p>

          <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.25 }}
            className="mt-10 inline-block p-5 border border-[#1c1c1c]" style={{ background: '#050505' }}>
            <SaleQRCode value={CARD_BUY_URL} size={260} />
          </motion.div>

          <p className="text-[11px] font-mono mt-6 break-all" style={{ color: CY }} data-testid="card-sale-url">{CARD_BUY_URL}</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#555] mt-2">£20 per card · GBP · Mr. Relative</p>

          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <button onClick={copy} data-testid="card-sale-copy" style={{ cursor: 'pointer' }}
              className="inline-flex items-center gap-2 border border-[#2a2a2a] text-[#ccc] hover:text-white hover:border-[#90E0EF]/50 px-4 py-3 text-[10px] font-mono uppercase tracking-[0.16em] transition-colors">
              <Copy className="w-3.5 h-3.5" /> {copied ? 'Copied' : 'Copy Link'}
            </button>
            <a href="/cards/buy" target="_blank" rel="noreferrer" data-testid="card-sale-open"
              className="inline-flex items-center gap-2 border border-[#2a2a2a] text-[#ccc] hover:text-white hover:border-[#90E0EF]/50 px-4 py-3 text-[10px] font-mono uppercase tracking-[0.16em] transition-colors">
              <ExternalLink className="w-3.5 h-3.5" /> Open Checkout
            </a>
            <a href="/cards/qr" target="_blank" rel="noreferrer" data-testid="card-sale-print"
              style={{ background: CY, color: '#050505' }}
              className="inline-flex items-center gap-2 px-4 py-3 text-[10px] font-mono uppercase tracking-[0.16em] font-medium">
              <Printer className="w-3.5 h-3.5" /> Print QR
            </a>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
